import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Info12Regular, Dismiss12Regular, LockClosed12Regular, Warning12Filled, ChevronRight12Regular, ChevronDown12Regular, ArrowRight16Regular } from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody, buildInvestmentOpinionJournalBody } from '../../../utils/askLensJournal';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import { resolveEvidenceObjectTarget, resolveEvidencePanelScopeArea } from '../../../utils/intelligenceInsightsModuleViewModels';
import {
    factorExpandDetail,
    factorExpandLabel,
    filterSupportedInsightsActions,
    hasDistinctFactorExpandDetail,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';
import drawerStyles from './intelligenceInsightsHighlights.module.css';

const POSTURE_BADGE = {
    amber: styles.dealPostureBadgeAmber,
    green: styles.dealPostureBadgeGreen,
    red: styles.dealPostureBadgeRed,
    grey: styles.dealPostureBadgeGrey,
};

function materialityTagClass(materiality) {
    const m = String(materiality || '').toLowerCase();
    if (m.includes('material') || m.includes('high')) return styles.ioTagMaterial;
    return styles.ioTagNotable;
}

function posturePrefill(data) {
    return [data?.posture?.headline, data?.posture?.summary, data?.investmentView?.summary].filter(Boolean).join('\n\n');
}

function factorPrefill(item) {
    return [item?.title, item?.body, item?.consequence, item?.relevance].filter(Boolean).join('\n\n');
}

function conditionPrefill(item) {
    return [item?.title, item?.body, item?.affectedPosture].filter(Boolean).join('\n\n');
}

function isTextLinkAction(key) {
    return ['view_evidence', 'open_evidence_panel', 'view_relevance', 'view_consequence', 'view_details'].includes(key);
}

function IconList({ items, icon, itemClass }) {
    if (!items?.length) return null;
    return (
        <ul className={styles.ioRationaleList}>
            {items.map(item => (
                <li key={item} className={itemClass || styles.ioRationaleItem}>
                    <span className={styles.ioRationaleIcon} aria-hidden>
                        {icon}
                    </span>
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

function RationaleDrawer({ open, posture, onClose, onAction, onOpenModule }) {
    if (!open || !posture) return null;
    const rationale = posture.rationale || {};
    const alt = rationale.alternativeScenarios || {};
    const evidenceCount = Number(rationale.evidenceCount || 0);

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <h2 className={styles.swotDetailTitle}>Investment Rationale</h2>
                        </div>
                        <button type="button" className={drawerStyles.drawerClose} onClick={onClose} aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.swotDetailBody}>
                        {rationale.criticalDependencies?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Critical Dependencies</p>
                                <IconList items={rationale.criticalDependencies} icon={<LockClosed12Regular />} />
                            </section>
                        ) : null}

                        {rationale.assumptions?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Assumptions</p>
                                <IconList items={rationale.assumptions} icon={<Info12Regular />} />
                            </section>
                        ) : null}

                        {rationale.implications?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Potential Transaction Implications</p>
                                <IconList items={rationale.implications} icon={<Warning12Filled />} itemClass={styles.ioRationaleItemWarn} />
                            </section>
                        ) : null}

                        {alt.supportive?.length || alt.cautious?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Alternative Posture Scenarios</p>
                                {alt.supportive?.length ? (
                                    <div className={styles.ioAltBoxSupport}>
                                        <p className={styles.ioAltTitle}>Could move towards Supportive if:</p>
                                        <ul className={styles.ioAltList}>
                                            {alt.supportive.map(item => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                                {alt.cautious?.length ? (
                                    <div className={styles.ioAltBoxCaution}>
                                        <p className={styles.ioAltTitle}>Could move towards Cautious if:</p>
                                        <ul className={styles.ioAltList}>
                                            {alt.cautious.map(item => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}
                            </section>
                        ) : null}

                        {rationale.valueCreationPriorities?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Value-creation Priorities</p>
                                <IconList items={rationale.valueCreationPriorities} icon={<ArrowRight16Regular />} itemClass={styles.ioRationaleItemPriority} />
                            </section>
                        ) : null}

                        {rationale.supportingObjects?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Supporting Objects</p>
                                <div className={styles.chipRow}>
                                    {rationale.supportingObjects.map(id => (
                                        <span key={id} className={styles.objectChip}>
                                            {id}
                                        </span>
                                    ))}
                                </div>
                                <button type="button" className={styles.ioResolveLink} onClick={() => onOpenModule?.('key_findings')}>
                                    Resolve in Key Findings
                                </button>
                            </section>
                        ) : null}

                        {rationale.constrainingObjects?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Constraining Objects</p>
                                <div className={styles.chipRow}>
                                    {rationale.constrainingObjects.map(id => (
                                        <span key={id} className={`${styles.objectChip} ${styles.objectChipWarn}`}>
                                            {id}
                                        </span>
                                    ))}
                                </div>
                                <button type="button" className={styles.ioResolveLinkWarn} onClick={() => onOpenModule?.('risks_signals')}>
                                    Resolve in Risks & Signals
                                </button>
                            </section>
                        ) : null}

                        <div className={styles.ioRationaleFooter}>
                            <span className={styles.swotDetailEvidenceCount}>
                                {evidenceCount > 0 ? `${evidenceCount} evidence reference${evidenceCount === 1 ? '' : 's'}` : 'Evidence available'}
                            </span>
                            <button type="button" className={styles.swotDetailEvidenceLink} onClick={() => onAction?.({ key: 'view_evidence', label: 'View evidence' })}>
                                View evidence →
                            </button>
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

function FactorColumn({ title, headClass, factors, expandedFactor, onToggleExpand, onAction }) {
    return (
        <div className={styles.ioSplitCol}>
            <div className={`${styles.ioSplitHead} ${headClass}`}>{title}</div>
            {factors.length ? (
                factors.map(factor => {
                    const isExpanded = expandedFactor?.id === factor.id;
                    const expandKey = expandedFactor?.key || null;
                    const detailText = isExpanded && hasDistinctFactorExpandDetail(factor, expandKey) ? factorExpandDetail(factor, expandKey) : null;
                    const detailLabel = isExpanded ? factorExpandLabel(expandKey) : null;
                    return (
                        <div key={factor.id} className={styles.ioFactorRow}>
                            <div className={styles.ioFactorTop}>
                                <p className={styles.ioFactorTitle}>{factor.title}</p>
                                {factor.materiality ? <span className={`${styles.ioTag} ${materialityTagClass(factor.materiality)}`}>{factor.materiality}</span> : null}
                            </div>
                            {factor.body ? <p className={styles.ioFactorBody}>{factor.body}</p> : null}
                            {(factor.actions || []).length ? (
                                <div className={styles.ioLinkRow}>
                                    {factor.actions.map(action => {
                                        const key = action.key || action.actionType;
                                        const expandAction = key === 'view_relevance' || key === 'view_consequence';
                                        const link = isTextLinkAction(key);
                                        const expanded = isExpanded && expandKey === key;
                                        return (
                                            <button
                                                key={`${factor.id}-${key}`}
                                                type="button"
                                                className={link ? styles.ioTextLink : styles.ioMiniBtn}
                                                onClick={() => {
                                                    if (expandAction) {
                                                        onToggleExpand(factor.id, key);
                                                        return;
                                                    }
                                                    onAction(action, {
                                                        text: factorPrefill(factor),
                                                        item: factor,
                                                        evidenceTarget: action.target,
                                                        objectType: action.target?.object_type,
                                                        objectId: action.target?.object_id,
                                                    });
                                                }}>
                                                {action.label}
                                                {expandAction ? expanded ? <ChevronDown12Regular aria-hidden /> : <ChevronRight12Regular aria-hidden /> : null}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : null}
                            {isExpanded && detailText ? (
                                <div className={styles.ioFactorExpand}>
                                    {detailLabel ? <p className={styles.ioFactorMetaLabel}>{detailLabel}</p> : null}
                                    <p className={styles.ioFactorMeta}>{detailText}</p>
                                </div>
                            ) : null}
                        </div>
                    );
                })
            ) : (
                <p className={styles.emptyNote} style={{ padding: '14px 16px' }}>
                    No items in this group.
                </p>
            )}
        </div>
    );
}

const IntelligenceInsightsInvestmentOpinion = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const [rationaleOpen, setRationaleOpen] = useState(false);
    const [expandedFactor, setExpandedFactor] = useState(null);
    const [expandedConditionId, setExpandedConditionId] = useState(null);
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    const handleAction = async (action, context = {}) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const contextText = context.text || posturePrefill(data);

        if (key === 'view_rationale' || key === 'view_details') {
            setEvidenceTarget(null);
            setRationaleOpen(true);
            return;
        }
        if (key === 'view_relevance' || key === 'view_consequence') {
            if (context.item?.id) {
                setExpandedFactor(current => (current?.id === context.item.id && current?.key === key ? null : { id: context.item.id, key }));
            }
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setRationaleOpen(false);
            const target = action.target || context.evidenceTarget || {};
            const { objectType, objectId } = resolveEvidenceObjectTarget(target, context);
            const extras = {
                lensConclusion: context.lensConclusion || null,
                reasoningSteps: context.reasoningSteps || null,
            };
            if (objectType && objectId) {
                setEvidenceTarget({
                    mode: 'object',
                    objectType,
                    objectId,
                    area: resolveEvidencePanelScopeArea({
                        data,
                        target,
                        context,
                        preferredKeys: context.scopeKeys || [],
                    }),
                    ...extras,
                });
                return;
            }
            setEvidenceTarget({
                mode: 'scope',
                area: resolveEvidencePanelScopeArea({
                    data,
                    target,
                    context,
                    preferredKeys: context.scopeKeys || [],
                }),
                ...extras,
            });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(contextText);
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(contextText);
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(contextText);
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Investment Opinion',
                    headline: context.headline || data?.posture?.headline || 'Investment Opinion',
                    bodyText: buildInsightsItemJournalBody(context.item) || buildInvestmentOpinionJournalBody(data, contextText),
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
        if (key === 'open_evidence_follow_ups') {
            onOpenModule?.('evidence_follow_ups');
            return;
        }
    };

    if (error || !data || data.isEmpty) {
        const failed = Boolean(error) || data?.status === 'failed' || data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'Investment Opinion'}
                subtitle={data?.subtitle || 'Technology-led interpretation of the transaction and investment case.'}
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="investment_opinion"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    const posture = data.posture || {};
    const postureActions = filterSupportedInsightsActions(
        posture.actions?.length
            ? posture.actions
            : [
                  { key: 'view_rationale', label: 'View rationale' },
                  { key: 'view_evidence', label: 'View evidence' },
                  { key: 'save_to_journal', label: 'Save to Journal' },
                  { key: 'add_to_raid', label: 'Add to RAID' },
              ],
        { moduleKey: 'investment_opinion' },
    );
    const badgeClass = POSTURE_BADGE[posture.tone] || styles.dealPostureBadgeAmber;
    const investmentRows = data.investmentView?.rows || [];
    const conditions = data.conditions || [];
    const insufficient = posture.value === 'insufficient_evidence';
    const disabled = data.applicability?.enabled === false;
    const postureEvidenceContext = {
        text: posturePrefill(data),
        evidenceTarget: postureActions.find(a => a.key === 'view_evidence')?.target,
        objectType: postureActions.find(a => a.key === 'view_evidence')?.target?.object_type,
        objectId: postureActions.find(a => a.key === 'view_evidence')?.target?.object_id,
        lensConclusion:
            `The available evidence supports a ${posture.label || 'Conditional'} investment posture` +
            `${posture.confidence ? ` with ${String(posture.confidence).replace(' · ', ', ').toLowerCase()}` : ''}.` +
            (conditions.length ? ` ${conditions.length} material condition${conditions.length === 1 ? '' : 's'} require resolution to confirm or improve the posture.` : ''),
        reasoningSteps: [
            {
                step: 1,
                statement: `Reviewed ${data.supportingFactors?.length || 0} supporting objects and ${data.materialConcerns?.length || 0} constraining objects.`,
            },
            {
                step: 2,
                statement: `Identified ${conditions.length} condition${conditions.length === 1 ? '' : 's'} that materially affect investment posture.`,
            },
            {
                step: 3,
                statement: posture.confidence
                    ? `Evidence coverage assessed from current analysis — ${posture.confidence}.`
                    : 'Evidence coverage assessed from the current analysis version.',
            },
        ],
    };

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}
                demoBadge={false}>
                {data.disclaimer ? (
                    <div className={styles.disclaimerBanner}>
                        <Info12Regular aria-hidden />
                        <span>{data.disclaimer}</span>
                    </div>
                ) : null}

                {disabled ? <p className={styles.emptyNote}>{data.applicability?.reason || 'Investment Opinion is not enabled for this project type.'}</p> : null}

                <section className={styles.dealPostureCard}>
                    <p className={styles.sectionTitle}>Deal posture</p>
                    <span className={`${styles.dealPostureBadge} ${badgeClass}`}>{posture.label}</span>
                    {posture.headline ? <p className={styles.dealPostureHeadline}>{posture.headline}</p> : null}
                    {typeof posture.summary === 'string' && posture.summary ? <p className={styles.dealPostureSummary}>{posture.summary}</p> : null}
                    {posture.confidence || data.confidence ? (
                        <p className={styles.confidence} style={{ marginTop: 10 }}>
                            {posture.confidence || data.confidence}
                        </p>
                    ) : null}
                    <div className={styles.ioPostureActions}>
                        {postureActions.map(action => {
                            const key = action.key;
                            const isEvidence = key === 'view_evidence' || key === 'open_evidence_panel';
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className={isEvidence ? styles.ioTextLink : styles.ioMiniBtn}
                                    onClick={() => handleAction(action, postureEvidenceContext)}>
                                    {action.label}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {!insufficient && ((data.supportingFactors || []).length || (data.materialConcerns || []).length) ? (
                    <div className={styles.ioSplit}>
                        <FactorColumn
                            title="Deal-supporting factors"
                            headClass={styles.ioSplitHeadSupport}
                            factors={data.supportingFactors || []}
                            expandedFactor={expandedFactor}
                            onToggleExpand={(id, key) => setExpandedFactor(current => (current?.id === id && current?.key === key ? null : { id, key }))}
                            onAction={handleAction}
                        />
                        <FactorColumn
                            title="Material concerns"
                            headClass={styles.ioSplitHeadConcern}
                            factors={data.materialConcerns || []}
                            expandedFactor={expandedFactor}
                            onToggleExpand={(id, key) => setExpandedFactor(current => (current?.id === id && current?.key === key ? null : { id, key }))}
                            onAction={handleAction}
                        />
                    </div>
                ) : null}

                {(typeof data.investmentView?.summary === 'string' && data.investmentView.summary) || investmentRows.length ? (
                    <section className={styles.investmentViewCard}>
                        <p className={styles.sectionTitle}>Investment view</p>
                        {typeof data.investmentView?.summary === 'string' && data.investmentView.summary ? (
                            <p className={styles.investmentViewSummary}>{data.investmentView.summary}</p>
                        ) : null}
                        {investmentRows.length ? (
                            <div style={{ marginTop: 12 }}>
                                {investmentRows.map(row => {
                                    const isYes = row.tone === 'positive' || String(row.value).toLowerCase() === 'yes';
                                    const isMuted = row.tone === 'muted';
                                    return (
                                        <div key={row.id} className={styles.ivRow}>
                                            <p className={styles.ivLabel}>{row.label}</p>
                                            {isYes ? (
                                                <span className={styles.ivYes}>{row.value}</span>
                                            ) : (
                                                <p className={`${styles.ivValue} ${isMuted ? styles.ivValueMuted : ''}`}>{row.value}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </section>
                ) : null}

                {!insufficient && conditions.length ? (
                    <section className={styles.conditionsCard}>
                        <div className={styles.conditionsHead}>
                            <p className={styles.sectionTitle} style={{ margin: 0 }}>
                                Conditions to resolve
                            </p>
                            <span className={styles.conditionsCount}>{conditions.length}</span>
                        </div>
                        {conditions.map((condition, idx) => {
                            const expanded = expandedConditionId === condition.id;
                            const actions = condition.actions?.length ? condition.actions : condition.action ? [condition.action] : [];
                            const collapsedAction = actions.find(a => !isTextLinkAction(a.key)) || actions[0];
                            return (
                                <div key={condition.id} className={`${styles.conditionRow} ${expanded ? styles.conditionRowActive : ''}`}>
                                    <button type="button" className={styles.conditionMain} onClick={() => setExpandedConditionId(expanded ? null : condition.id)}>
                                        <p className={styles.conditionIndex}>{idx + 1}</p>
                                        <div className={styles.conditionBody}>
                                            <div className={styles.conditionTitleRow}>
                                                <p className={styles.conditionTitle}>{condition.title}</p>
                                                {condition.priority ? <span className={`${styles.ioTag} ${styles.ioTagHigh}`}>{condition.priority}</span> : null}
                                                {collapsedAction ? (
                                                    <span className={styles.ioMiniBtn} role="presentation">
                                                        {collapsedAction.label}
                                                    </span>
                                                ) : null}
                                                {expanded ? <ChevronDown12Regular aria-hidden /> : <ChevronRight12Regular aria-hidden />}
                                            </div>
                                        </div>
                                    </button>
                                    {expanded ? (
                                        <div className={styles.conditionDetail}>
                                            {condition.body ? <p className={styles.ioFactorBody}>{condition.body}</p> : null}
                                            {condition.affectedPosture ? <p className={styles.conditionAffected}>Affected posture: {condition.affectedPosture}</p> : null}
                                            {condition.objectIds?.length ? (
                                                <div className={styles.chipRow}>
                                                    {condition.objectIds.map(id => (
                                                        <span key={id} className={styles.objectChip}>
                                                            {id}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                            {actions.length ? (
                                                <div className={styles.ioLinkRow}>
                                                    {actions.map(action => {
                                                        const key = action.key;
                                                        const link = isTextLinkAction(key);
                                                        return (
                                                            <button
                                                                key={`${condition.id}-${key}`}
                                                                type="button"
                                                                className={link ? styles.ioTextLink : styles.ioMiniBtn}
                                                                onClick={() =>
                                                                    handleAction(action, {
                                                                        text: conditionPrefill(condition),
                                                                        item: condition,
                                                                        evidenceTarget: action.target,
                                                                        objectType: action.target?.object_type,
                                                                        objectId: action.target?.object_id,
                                                                    })
                                                                }>
                                                                {action.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </section>
                ) : null}

                {data.footerBanner ? (
                    <div className={styles.footerBanner}>
                        <div>
                            <p className={styles.footerBannerTitle}>{data.footerBanner.title}</p>
                        </div>
                        <button type="button" className={styles.btnLink} onClick={() => onOpenModule?.(data.footerBanner.actionKey || 'evidence_follow_ups')}>
                            {data.footerBanner.actionLabel}
                        </button>
                    </div>
                ) : null}
            </InsightsModuleChrome>

            <RationaleDrawer
                open={rationaleOpen}
                posture={posture}
                onClose={() => setRationaleOpen(false)}
                onOpenModule={onOpenModule}
                onAction={action => handleAction(action, postureEvidenceContext)}
            />

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || null}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                variant="investment"
                lensConclusion={evidenceTarget?.lensConclusion || null}
                reasoningSteps={evidenceTarget?.reasoningSteps || null}
                evidenceCoverage={
                    String(posture.confidence || data.confidence || '')
                        .toLowerCase()
                        .includes('partial')
                        ? 'partial'
                        : String(posture.confidence || data.confidence || '')
                                .toLowerCase()
                                .includes('full')
                          ? 'full'
                          : 'partial'
                }
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        text: panelCard?.headline || posturePrefill(data),
                        evidenceArea: evidenceTarget?.area,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsInvestmentOpinion;
